/**
 * File documents that already exist into this collection.
 *
 * The recovery path for everything uploaded before filing-after-upload was possible.
 * Those documents sit in Pustaka with no collection and, until now, no way into one —
 * delete and re-upload was the only correction, which on a scanned PDF means waiting for
 * OCR a second time.
 *
 * Filing is one PATCH per document rather than one bulk call, because there is no bulk
 * route and inventing one would hide partial failure: eleven documents filed and one
 * refused must not report as "done". The count that comes back is what actually moved.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input, Panel } from "@/components/primitives.tsx";
import { ApiError, api } from "@/lib/api.ts";
import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";

const CANDIDATE_LIMIT = 50;

/**
 * What this collection can still be given.
 *
 * Documents ALREADY filed here are dropped: the corpus route has no "not in this
 * collection" filter, and a tick that files a document where it already sits looks
 * broken. Documents filed ELSEWHERE stay — moving one is legitimate, and the row says
 * where it currently lives so it is never a surprise.
 */
export function filingCandidates<T extends { collection_id?: string | null }>(
  documents: readonly T[],
  collectionId: string,
): T[] {
  return documents.filter((document) => document.collection_id !== collectionId);
}

export function AddDocumentsDialog({
  collectionId,
  collectionName,
  onClose,
  onAdded,
}: {
  collectionId: string;
  collectionName: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const candidates = useQuery({
    queryKey: ["corpus", "candidates", search],
    queryFn: () => api.corpus({ limit: CANDIDATE_LIMIT, search: search || undefined }),
  });

  const rows = filingCandidates(candidates.data?.documents ?? [], collectionId);

  const file = useMutation({
    mutationFn: async () => {
      const ids = [...picked];
      const results = await Promise.allSettled(
        ids.map((id) => api.setDocumentCollection(id, collectionId)),
      );
      const failed = results.filter((result) => result.status === "rejected").length;
      return { filed: ids.length - failed, failed };
    },
    onSuccess: (result) => {
      onAdded();
      // Only close when everything landed. A dialog that vanishes on partial failure is
      // how a reader concludes all twelve were filed when eleven were.
      if (result.failed === 0) onClose();
      setPicked(new Set());
    },
  });

  function toggle(id: string) {
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      className="scrim open"
      role="dialog"
      aria-modal="true"
      aria-label={t("collection.addDocuments")}
    >
      <div className="panel panel-accent" style={dialogStyle}>
        <div className="panel-title">
          {t("collection.addDocumentsTo", { name: collectionName })}
        </div>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("library.search")}
          aria-label={t("library.search")}
          autoFocus
          style={{ marginTop: "var(--s3)" }}
        />

        <div className="pick-list">
          {candidates.isPending ? <p className="hint">{t("common.loading")}</p> : null}
          {!candidates.isPending && rows.length === 0 ? (
            <p className="hint">{t("collection.noCandidates")}</p>
          ) : null}
          {rows.map((document) => {
            const parsed = parseLegalTitle(document.title);
            return (
              <label key={document.id} className="pick-row">
                <input
                  type="checkbox"
                  checked={picked.has(document.id)}
                  onChange={() => toggle(document.id)}
                />
                <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                  {/* Label ABOVE subject, the same stacked treatment the table gives —
                      two thirds of these titles run past 120 characters and 43% are
                      ALL-CAPS. Inline, the label ran straight into the subject with no
                      gap: "…Simeulue 2/2019Pembentukan Panitia Khusus". */}
                  <span className="pick-title" title={parsed.full}>
                    {parsed.label ? (
                      <span className="mono doc-cell-label">{parsed.label}</span>
                    ) : null}
                    <span>{truncateAtWord(parsed.subject, 90)}</span>
                  </span>
                  <span className="faint" style={{ fontSize: 12.5 }}>
                    {document.source_type} · {document.format}
                    {/* Where it is NOW. Moving a filed document is legitimate, but it
                        should never be a surprise. */}
                    {document.collection_id ? ` · ${t("collection.alreadyFiled")}` : ""}
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        {file.error ? (
          <Panel tone="red" title={t("errors.generic")}>
            {file.error instanceof ApiError ? file.error.message : null}
          </Panel>
        ) : null}
        {file.data && file.data.failed > 0 ? (
          <Panel tone="red" title={t("collection.addPartial", { ...file.data })} />
        ) : null}

        <div className="row" style={{ marginTop: "var(--s4)" }}>
          <span className="faint">{t("collection.picked", { count: picked.size })}</span>
          <span className="spacer" />
          <Button onClick={onClose}>{t("collections.cancel")}</Button>
          <Button
            variant="primary"
            onClick={() => file.mutate()}
            disabled={picked.size === 0 || file.isPending}
          >
            {file.isPending ? t("collection.adding") : t("collection.addSubmit")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--surface)",
  maxWidth: 620,
  margin: "10vh auto 0",
  boxShadow: "0 24px 60px rgb(20 22 26 / 0.22)",
};
